import type { TenantAdminDTO } from '@/entities/tenant-admin'
import { format, parseISO } from 'date-fns'
import { KeyRound, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantAdminsQuery } from '@/entities/tenant-admin'
import { TenantAdminCreateDialog } from '@/features/tenant/tenant-admin-management/tenant-admin-create-dialog'
import { TenantAdminPasswordDialog } from '@/features/tenant/tenant-admin-management/tenant-admin-password-dialog'
import { useCanManageTenantAdmins } from '@/shared/hooks/use-can-manage-tenant-admins'
import { isSuperAdminRole } from '@/shared/libs/admin-routes'
import { useAuthStore } from '@/shared/store/auth-store'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Skeleton } from '@/shared/ui/skeleton'
import { StatusBadge } from '@/shared/ui/status-badge'

interface TenantAdminManagementProps {
  tenantId: string
  tenantName?: string | undefined
}

const SKELETON_ROW_COUNT = 2
const MAX_INITIALS = 2

function getDisplayName(admin: TenantAdminDTO): string {
  const fullName = admin.fullName?.trim()
  if (fullName) return fullName
  return admin.username
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, MAX_INITIALS)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

function formatCreatedAt(createdAt: string): string | null {
  try {
    return format(parseISO(createdAt), 'dd/MM/yyyy')
  } catch {
    return null
  }
}

export function TenantAdminManagement({ tenantId, tenantName }: TenantAdminManagementProps) {
  const { t } = useTranslation()
  const [createOpen, setCreateOpen] = useState(false)
  const [passwordTarget, setPasswordTarget] = useState<TenantAdminDTO | null>(null)
  const canManage = useCanManageTenantAdmins(tenantId)
  const isSuperAdmin = useAuthStore((state) => isSuperAdminRole(state.user?.role))
  const { data, isLoading, isError, refetch, isRefetching } = useTenantAdminsQuery(tenantId)

  const admins = data?.items ?? []

  return (
    <section className="border border-zinc-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-zinc-900">
            {t('tenant.admin.title')}
            {data ? (
              <span className="ml-2 text-xs font-normal text-zinc-500">
                {t('tenant.admin.count', { total: data.total })}
              </span>
            ) : null}
          </h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            {canManage
              ? t('tenant.admin.descriptionManage')
              : t('tenant.admin.descriptionReadonly')}
          </p>
        </div>
        {canManage ? (
          <Button
            size="sm"
            className="shrink-0 self-start sm:self-auto"
            onClick={() => setCreateOpen(true)}
          >
            <Plus />
            {t('tenant.admin.create')}
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <ul className="divide-y divide-zinc-100" aria-busy="true">
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <li key={index} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="h-9 w-9 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-56" />
              </div>
            </li>
          ))}
        </ul>
      ) : isError ? (
        <div
          role="alert"
          className="flex flex-col items-start gap-3 px-4 py-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="text-sm text-red-700">{t('tenant.admin.loadError')}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isRefetching}
          >
            {t('tenant.admin.retry')}
          </Button>
        </div>
      ) : admins.length === 0 ? (
        <EmptyState
          title={t('tenant.admin.empty')}
          {...(canManage ? { description: t('tenant.admin.emptyDescription') } : {})}
        />
      ) : (
        <ul className="divide-y divide-zinc-100">
          {admins.map((admin) => {
            const name = getDisplayName(admin)
            const createdAt = formatCreatedAt(admin.createdAt)
            // Mirrors IAM: the head admin's password is a super-admin matter, the rest is the owner's.
            const canChangePassword = canManage && (isSuperAdmin || !admin.isTenantOwner)
            return (
              <li
                key={admin.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={admin.avatarUrl ?? undefined} alt="" />
                    <AvatarFallback className="text-xs font-medium text-zinc-600">
                      {getInitials(name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="truncate text-sm font-medium text-zinc-900">{name}</span>
                      {admin.isTenantOwner ? (
                        <Badge variant="outline" className="text-zinc-700">
                          {t('tenant.admin.owner')}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-zinc-500">
                      {`@${admin.username}`}
                      {admin.email ? ` · ${admin.email}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pl-12 text-xs text-zinc-500 sm:pl-0">
                  <StatusBadge
                    status={admin.isActive ? 'active' : 'inactive'}
                    label={admin.isActive ? t('common.active') : t('common.inactive')}
                  />
                  {createdAt ? (
                    <span>{t('tenant.admin.createdOn', { date: createdAt })}</span>
                  ) : null}
                  {canChangePassword ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto sm:ml-0"
                      onClick={() => setPasswordTarget(admin)}
                      aria-label={`${t('tenant.admin.changePassword.action')} ${admin.username}`}
                    >
                      <KeyRound />
                      {t('tenant.admin.changePassword.action')}
                    </Button>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {canManage ? (
        <TenantAdminCreateDialog
          open={createOpen}
          tenantId={tenantId}
          tenantName={tenantName}
          onOpenChange={setCreateOpen}
        />
      ) : null}
      {canManage ? (
        <TenantAdminPasswordDialog
          admin={passwordTarget}
          tenantId={tenantId}
          onOpenChange={(open) => {
            if (!open) setPasswordTarget(null)
          }}
        />
      ) : null}
    </section>
  )
}
